import { apiClient } from './apiClient';

export const mantenimientoService = {
  _findDuplicateTareaIds: (rows = []) => {
    const tareas = Array.isArray(rows) ? rows : [];
    const seen = new Set();
    const duplicateIds = [];
    const keptRows = [];

    for (const t of tareas) {
      const key = mantenimientoService._taskUniqueKey(t);
      if (seen.has(key)) {
        if (t?.id) duplicateIds.push(t.id);
        continue;
      }
      seen.add(key);
      keptRows.push(t);
    }

    return { duplicateIds, keptRows };
  },

  // Listar mantenimientos con filtros y paginación
  listar: async (params = {}) => {
  // Usa HistorialMantenimientoViewSet
  const response = await apiClient.get('/historial-mantenimiento/', { params });
    return response.data;
  },

  // Listar todos los mantenimientos (todas las páginas)
  listarTodos: async (params = {}) => {
    let url = '/historial-mantenimiento/';
    let firstParams = params;
    const items = [];
    while (url) {
      const resp = await apiClient.get(url, { params: firstParams });
      const data = resp.data;
      if (Array.isArray(data)) {
        // Endpoint sin paginar
        return data;
      }
      const pageItems = data.results || [];
      items.push(...pageItems);
      // next puede ser absoluta; axios soporta URLs absolutas
      url = data.next || null;
      firstParams = undefined; // solo en la primera petición
    }
    return items;
  },

  // Completa el listado a partir de una primera página ya descargada: pide las
  // páginas restantes en paralelo (en vez de una tras otra) y no vuelve a pedir la
  // primera. Con `count` y el tamaño de página sabemos cuántas faltan.
  completarPaginas: async (primeraPagina, params = {}, pageSize = 150) => {
    const primeros = primeraPagina?.results || [];
    const total = Number(primeraPagina?.count) || 0;
    const paginas = Math.ceil(total / pageSize);
    if (!primeraPagina?.next || paginas <= 1) return primeros;
    const pedidos = [];
    for (let page = 2; page <= paginas; page += 1) {
      pedidos.push(apiClient.get('/historial-mantenimiento/', { params: { ...params, page, page_size: pageSize } }));
    }
    const respuestas = await Promise.all(pedidos);
    return respuestas.reduce((acc, r) => acc.concat(r.data?.results || []), [...primeros]);
  },

  // Obtener un mantenimiento específico
  obtener: async (id) => {
    const response = await apiClient.get(`/historial-mantenimiento/${id}/`);
    return response.data;
  },

  // La OT activa se almacena en historial-mantenimiento.
  obtenerOrdenTrabajoActiva: async (ordenTrabajoId) => {
    const response = await apiClient.get(`/historial-mantenimiento/${ordenTrabajoId}/`);
    return response.data;
  },

  // Crear nuevo mantenimiento
  crear: async (data) => {
    const response = await apiClient.post('/historial-mantenimiento/', data);
    return response.data;
  },

  // Actualizar mantenimiento existente
  actualizar: async (id, data) => {
    const response = await apiClient.put(`/historial-mantenimiento/${id}/`, data);
    return response.data;
  },

  // Actualización parcial (PATCH) para editar campos puntuales sin enviar todo el objeto
  actualizarParcial: async (id, data) => {
    const response = await apiClient.patch(`/historial-mantenimiento/${id}/`, data);
    return response.data;
  },

  // Eliminar mantenimiento
  eliminar: async (id) => {
    await apiClient.delete(`/historial-mantenimiento/${id}/`);
  },

  // Completar mantenimiento
  completar: async (id, data = {}) => {
    if (!id) {
      // Item calculado: programar y completar en una sola operación
      const response = await apiClient.post('/historial-mantenimiento/programar-completar/', data);
      return response.data;
    }
    const response = await apiClient.patch(`/historial-mantenimiento/${id}/`, { ...data, estado: 'completado' });
    return response.data;
  },

  // Cancelar mantenimiento
  cancelar: async (id, motivo = '', extra = {}) => {
    if (!id) {
      // Para items calculados necesitamos datos para programar primero
      const payload = {
        motivo,
        equipo: extra.equipo,
        rutina: extra.rutina,
        fecha_programada: extra.fecha_programada || null,
      };
      if (extra.odometro_programado !== '' && extra.odometro_programado !== null && extra.odometro_programado !== undefined) {
        payload.odometro_programado = extra.odometro_programado;
      }
      const response = await apiClient.post('/historial-mantenimiento/programar-cancelar/', payload);
      return response.data;
    }
    const response = await apiClient.patch(`/historial-mantenimiento/${id}/`, { estado: 'cancelado', observaciones_realizacion: motivo });
    return response.data;
  },

  // Programar mantenimiento (para items calculados o creación explícita)
  programar: async (data = {}) => {
    const response = await apiClient.post('/historial-mantenimiento/programar/', data);
    return response.data;
  },

  // Reprogramar visual (override) para items calculados sin afectar historial
  reprogramarVisual: async (data = {}) => {
    const response = await apiClient.post('/historial-mantenimiento/reprogramar/', data);
    return response.data;
  },

  // Limpiar reprogramación visual (p.ej. si luego se programa/cancela/completa)
  limpiarReprogramacionVisual: async (data = {}) => {
    const response = await apiClient.post('/historial-mantenimiento/reprogramar-clear/', data);
    return response.data;
  },

  // Obtener historial de equipo
  obtenerHistorial: async (equipoId) => {
    const response = await apiClient.get(`/historial-mantenimiento/`, { params: { equipo: equipoId } });
    return response.data.results || response.data;
  },

  // Obtener opciones para selects — lite=true: trae TODOS los equipos igual,
  // pero con el serializer liviano (sin campos calculados que pegan a la BD)
  obtenerEquipos: async () => {
    // page_size explícito: esta función devuelve la lista completa y quien la
    // llama la usa entera. Sin el parámetro dependía del default del backend, y
    // el día que ese default baje se quedaría con la primera página sin que
    // nadie se entere: un selector de equipos incompleto y ningún error.
    const response = await apiClient.get('/equipos/', { params: { lite: 'true', page_size: 5000 } });
    return response.data.results || response.data;
  },

  obtenerRutinas: async () => {
    const response = await apiClient.get('/rutinas-mantenimiento/', { params: { page_size: 5000 } });
    return response.data.results || response.data;
  },

  obtenerMecanicos: async () => {
    const response = await apiClient.get('/mecanicos/', { params: { page_size: 9999 } });
    return response.data.results || response.data;
  },

  // Mecánicos OTP (tabla dedicada, análogo a otc-mecanicos)
  listarMecanicosOtp: async (historialId) => {
    const response = await apiClient.get('/otp-mecanicos/', { params: { historial: historialId, page_size: 9999 } });
    return response.data.results || response.data || [];
  },

  crearMecanicoOtp: async (payload) => {
    const response = await apiClient.post('/otp-mecanicos/', payload);
    return response.data;
  },

  actualizarMecanicoOtp: async (id, payload) => {
    const response = await apiClient.patch(`/otp-mecanicos/${id}/`, payload);
    return response.data;
  },

  eliminarMecanicoOtp: async (id) => {
    await apiClient.delete(`/otp-mecanicos/${id}/`);
  },

  // Gastos extra OTP (tabla dedicada, análogo a otc-gastos-extra)
  listarGastosExtraOtp: async (historialId) => {
    const response = await apiClient.get('/otp-gastos-extra/', { params: { historial: historialId, page_size: 9999 } });
    return response.data.results || response.data || [];
  },

  crearGastoExtraOtp: async (payload) => {
    const response = await apiClient.post('/otp-gastos-extra/', payload);
    return response.data;
  },

  actualizarGastoExtraOtp: async (id, payload) => {
    const response = await apiClient.patch(`/otp-gastos-extra/${id}/`, payload);
    return response.data;
  },

  eliminarGastoExtraOtp: async (id) => {
    await apiClient.delete(`/otp-gastos-extra/${id}/`);
  },

  // Repuestos adicionales OTP (entregados por almacén, no planificados en la rutina)
  listarRepuestosAdicionalesOtp: async (historialId) => {
    const response = await apiClient.get('/otp-repuestos-adicionales/', { params: { historial: historialId, page_size: 9999 } });
    return response.data.results || response.data || [];
  },

  crearRepuestoAdicionalOtp: async (payload) => {
    const response = await apiClient.post('/otp-repuestos-adicionales/', payload);
    return response.data;
  },

  actualizarRepuestoAdicionalOtp: async (id, payload) => {
    const response = await apiClient.patch(`/otp-repuestos-adicionales/${id}/`, payload);
    return response.data;
  },

  eliminarRepuestoAdicionalOtp: async (id) => {
    await apiClient.delete(`/otp-repuestos-adicionales/${id}/`);
  },

  // Obtener/Modificar PLANILLA ACTIVA (editable) para una OT/mantenimiento programado
  obtenerPlanillaActiva: async (ordenTrabajoId) => {
    const response = await apiClient.get('/planillas-activas/por-historial/', { params: { historial: ordenTrabajoId } });
    return response.data || null;
  },

  crearPlanilla: async (data) => {
    const response = await apiClient.post('/planillas-activas/', data);
    return response.data;
  },

  iniciarPlanilla: async (planillaId, iniciadoEn = null) => {
    const response = await apiClient.post(`/planillas-activas/${planillaId}/iniciar/`, iniciadoEn ? { iniciado_en: iniciadoEn } : {});
    return response.data;
  },

  obtenerTareasPorPlanillaActivaId: async (planillaId, options = {}) => {
    const { sync = false } = options;
    const response = await apiClient.get('/planillas-activas-tareas/', {
      params: { planilla: planillaId, page_size: 9999, ...(sync ? { sync: 1 } : {}) },
    });
    const rows = response.data.results || response.data || [];
    return (Array.isArray(rows) ? rows : []).map(r => ({ ...r, origen_api: 'planilla_activa' }));
  },

  _taskUniqueKey: (row = {}) => {
    const tareaRaw = row?.tarea;
    const tareaId = (typeof tareaRaw === 'object' && tareaRaw !== null) ? tareaRaw.id : tareaRaw;
    const orden = Number(row?.orden ?? 0);
    const nombre = String(row?.tarea_nombre || row?.nombre_display || row?.nombre || '').trim().toLowerCase();
    return `${orden}|${tareaId || ''}|${nombre}`;
  },

  _dedupeTareasPlanillaActiva: async (planillaId, tareas = null) => {
    let sourceRows = tareas;
    if (!Array.isArray(sourceRows)) {
      const response = await apiClient.get('/planillas-activas-tareas/', { params: { planilla: planillaId, page_size: 9999 } });
      sourceRows = response.data.results || response.data || [];
    }

    const { duplicateIds, keptRows } = mantenimientoService._findDuplicateTareaIds(sourceRows);

    if (duplicateIds.length) {
      await Promise.allSettled(
        duplicateIds.map((dupId) => apiClient.delete(`/planillas-activas-tareas/${dupId}/`))
      );
    }

    return { removed: duplicateIds.length, keptRows };
  },

  crearTareaPlanillaActiva: async (data) => {
    const response = await apiClient.post('/planillas-activas-tareas/', data);
    return response.data;
  },

  eliminarTareaPlanillaActiva: async (id) => {
    await apiClient.delete(`/planillas-activas-tareas/${id}/`);
  },

  _bootstrapPlanillaActivaDesdeHistorico: async (ordenTrabajoId, planillaId) => {
    const existentes = await mantenimientoService.obtenerTareasPorPlanillaActivaId(planillaId);
    const existingKeys = new Set((existentes || []).map(t => mantenimientoService._taskUniqueKey(t)));

    const response = await apiClient.get('/planillas-historicas-tareas/', { params: { historial: ordenTrabajoId, page_size: 9999 } });
    const snapshot = response.data.results || response.data || [];
    for (const t of (Array.isArray(snapshot) ? snapshot : [])) {
      const payload = {
        planilla: planillaId,
        tarea: t.tarea || null,
        tarea_nombre: t.tarea_nombre || null,
        componente: t.componente || null,
        orden: t.orden || 0,
        tiempo_estimado_minutos: t.tiempo_estimado_minutos,
        requiere_repuestos: !!(t.requiere_repuestos || t.requiere_repuestos_display),
        realizado: !!t.realizado,
        horas_hombre: t.horas_hombre,
        repuesto: t.repuesto || null,
        repuesto_codigo: t.repuesto_codigo || null,
        repuesto_descripcion: t.repuesto_descripcion || null,
        cantidad_usada: t.cantidad_usada,
        gastos_extra: t.gastos_extra || 0,
        observaciones: t.observaciones || null,
        consumibles: Array.isArray(t.consumibles) ? t.consumibles : [],
      };

      const key = mantenimientoService._taskUniqueKey(payload);
      if (existingKeys.has(key)) {
        continue;
      }

      await apiClient.post('/planillas-activas-tareas/', {
        ...payload,
      });
      existingKeys.add(key);
    }
  },

  _bootstrapPlanillaActivaDesdeRutina: async (ordenTrabajoId, planillaId) => {
    const existentes = await mantenimientoService.obtenerTareasPorPlanillaActivaId(planillaId);
    const existingKeys = new Set((existentes || []).map(t => mantenimientoService._taskUniqueKey(t)));

    const hmResp = await apiClient.get(`/historial-mantenimiento/${ordenTrabajoId}/`);
    const hm = hmResp.data || {};
    const rutinaId = hm?.rutina;
    if (!rutinaId) return;

    const rtResp = await apiClient.get('/rutinas-tareas/', { params: { rutina: rutinaId, page_size: 9999 } });
    const rutinaTareas = rtResp.data?.results || rtResp.data || [];
    for (const t of (Array.isArray(rutinaTareas) ? rutinaTareas : [])) {
      const tareaObj = t?.tarea && typeof t.tarea === 'object' ? t.tarea : null;
      const componenteObj = t?.componente_display && typeof t.componente_display === 'object' ? t.componente_display : null;
      const componenteRaw = t?.componente;
      const componenteId = componenteObj?.id || (typeof componenteRaw === 'object' ? componenteRaw?.id : componenteRaw) || null;
      const payload = {
        planilla: planillaId,
        tarea: t.tarea || tareaObj?.id || null,
        tarea_nombre: t.tarea_nombre || t.nombre_display || t.nombre || null,
        componente: componenteId,
        orden: t.orden || 0,
        tiempo_estimado_minutos: t.tiempo_estimado_minutos ?? t.tiempo_estimado_minutos_display ?? t.tiempo_estimado_override ?? null,
        requiere_repuestos: !!(t.requiere_repuestos || t.requiere_repuestos_display),
        realizado: false,
        horas_hombre: null,
        repuesto: null,
        repuesto_codigo: null,
        repuesto_descripcion: null,
        cantidad_usada: null,
        gastos_extra: 0,
        observaciones: t.observaciones || t.observaciones_display || t.observaciones_adicionales || null,
        consumibles: [],
      };

      const key = mantenimientoService._taskUniqueKey(payload);
      if (existingKeys.has(key)) {
        continue;
      }

      await apiClient.post('/planillas-activas-tareas/', {
        ...payload,
      });
      existingKeys.add(key);
    }
  },

  obtenerTareasPlanillaActiva: async (ordenTrabajoId, equipoId = null, planillaPreloaded = null) => {
    // NUEVO FLUJO: usar planilla activa persistida en backend (sin guardado local)
    let planilla = planillaPreloaded;
    if (!planilla) {
      planilla = await mantenimientoService.obtenerPlanillaActiva(ordenTrabajoId);
    }
    if (!planilla && equipoId) {
      try {
        planilla = await mantenimientoService.crearPlanilla({ historial: ordenTrabajoId, equipo: equipoId, estado: 'editable' });
      } catch (e) {
        // Si otra request creó la planilla primero, reintentar lectura.
        planilla = await mantenimientoService.obtenerPlanillaActiva(ordenTrabajoId);
      }
    }

    if (planilla?.id) {
      let tareasPlanilla = await mantenimientoService.obtenerTareasPorPlanillaActivaId(planilla.id);
      const cleanupInicial = await mantenimientoService._dedupeTareasPlanillaActiva(planilla.id, tareasPlanilla);
      if (cleanupInicial.removed > 0) {
        tareasPlanilla = cleanupInicial.keptRows || [];
      }

      if (!tareasPlanilla.length) {
        await mantenimientoService._bootstrapPlanillaActivaDesdeHistorico(ordenTrabajoId, planilla.id);
        tareasPlanilla = await mantenimientoService.obtenerTareasPorPlanillaActivaId(planilla.id);
        const cleanupHistorico = await mantenimientoService._dedupeTareasPlanillaActiva(planilla.id, tareasPlanilla);
        if (cleanupHistorico.removed > 0) {
          tareasPlanilla = cleanupHistorico.keptRows || [];
        }
      }
      if (!tareasPlanilla.length) {
        await mantenimientoService._bootstrapPlanillaActivaDesdeRutina(ordenTrabajoId, planilla.id);
        tareasPlanilla = await mantenimientoService.obtenerTareasPorPlanillaActivaId(planilla.id);
        const cleanupRutina = await mantenimientoService._dedupeTareasPlanillaActiva(planilla.id, tareasPlanilla);
        if (cleanupRutina.removed > 0) {
          tareasPlanilla = cleanupRutina.keptRows || [];
        }
      }
      return tareasPlanilla;
    }

    // Fallback histórico (solo lectura)
    const response = await apiClient.get('/planillas-historicas-tareas/', { params: { historial: ordenTrabajoId, page_size: 9999 } });
    const rows = response.data.results || response.data;
    return (Array.isArray(rows) ? rows : []).map(r => ({ ...r, origen_api: 'planilla_historica' }));
  },

  actualizarTareaPlanillaActiva: async (id, data, origen = 'planilla_activa') => {
    if (origen === 'planilla_historica' || origen === 'historial') {
      throw new Error('Escritura legacy deshabilitada: use Planilla/PlanillaTarea.');
    }
    const response = await apiClient.patch(`/planillas-activas-tareas/${id}/`, data);
    return response.data;
  },

  // ===========================
  // Compatibilidad (aliases legacy)
  // ===========================
  obtenerPlanillaPorHistorial: async (historialId) => mantenimientoService.obtenerPlanillaActiva(historialId),
  obtenerTareasPlanilla: async (planillaId) => mantenimientoService.obtenerTareasPorPlanillaActivaId(planillaId),
  crearTareaPlanilla: async (data) => mantenimientoService.crearTareaPlanillaActiva(data),
  _bootstrapPlanillaDesdeHistorial: async (historialId, planillaId) => mantenimientoService._bootstrapPlanillaActivaDesdeHistorico(historialId, planillaId),
  obtenerTareasHistorial: async (historialId, equipoId = null) => mantenimientoService.obtenerTareasPlanillaActiva(historialId, equipoId),
  actualizarTareaHistorial: async (id, data, origen = 'planilla') => mantenimientoService.actualizarTareaPlanillaActiva(id, data, origen === 'historial' ? 'planilla_historica' : 'planilla_activa'),

  obtenerPlanes: async () => {
    const response = await apiClient.get('/planes-mantenimiento/');
    return response.data.results || response.data;
  },

  obtenerSucursales: async () => {
    const response = await apiClient.get('/sucursales/');
    return response.data.results || response.data;
  },

  // Próximos mantenimientos (pendientes próximos 7 días) y vencidos
  proximos: async (options = {}) => {
    const { fast = false } = options;
    const response = await apiClient.get('/historial-mantenimiento/proximos/', {
      params: fast ? { fast: 1 } : undefined,
      timeout: fast ? 15000 : 45000,
    });
    const data = response.data;
    // Manejar posibles formas de respuesta: array (no paginado) o objeto paginado { results: [...] }
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    // Fallback: devolver el objeto tal cual
    return data;
  },

  // Alias usado por algunos componentes existentes
  obtenerMantenimientos: async (params = {}) => {
    const data = await mantenimientoService.listar(params);
    return data.results || data;
  },

  // Adjuntos
  subirAdjunto: async (historialId, formData) => {
    const response = await apiClient.post(`/historial-mantenimiento/${historialId}/adjuntar/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  eliminarAdjunto: async (historialId, adjuntoId) => {
    await apiClient.delete(`/historial-mantenimiento/${historialId}/adjuntos/${adjuntoId}/`);
  },

  editarAdjunto: async (historialId, adjuntoId, formData) => {
    const response = await apiClient.patch(`/historial-mantenimiento/${historialId}/adjuntos/${adjuntoId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default mantenimientoService;
